/**
 * Header pill: the open project is also open on another computer or window
 * (OPBridge S350, operator 2026-11-03: "whenever any document is open on two
 * different computers, a warning must be displayed"). State lives in
 * `layout/docPresence.ts`, which has the operator's six answers.
 *
 * In the bar rather than over the canvas on purpose: the same project open on
 * the iPad and a desktop can be normal through a whole set, and a banner over
 * the top row of controls would cost more than the warning is worth. The bar
 * holds the warning without moving a single control.
 *
 *   warn     - "Also open on STREAMPC"; the window that opened it second also
 *              gets "Open read-only".
 *   readonly - "Read-only - open on STREAMPC", with "Edit anyway".
 *   quiet    - only a computer that could not be checked: "Couldn't check
 *              STREAMPC", dim, no button.
 */

import { Show } from 'solid-js';
import { docPresence, openersPhrase, openReadOnly, editAnyway } from '../layout/docPresence.ts';
import { projectReadOnly } from '../layout/readOnly.ts';

export function DocOpenPill() {
  const p = () => docPresence();
  const elsewhere = () => (p()?.opens.length ?? 0) > 0;
  const state = (): 'warn' | 'readonly' | 'quiet' | 'none' => {
    if (projectReadOnly()) return 'readonly';
    if (elsewhere()) return 'warn';
    return (p()?.unchecked.length ?? 0) > 0 ? 'quiet' : 'none';
  };
  const who = () => openersPhrase(p()?.opens ?? []);
  return (
    <Show when={state() !== 'none'}>
      <div class="doc-open-pill" data-state={state()} role="status">
        <span class="doc-open-pill-mark" aria-hidden="true" />
        <span class="doc-open-pill-msg">
          {/* Short on purpose: the computer's name is the word that matters, and the
              bar is shared (measured: "Also open on STREA..." truncated the name). */}
          {state() === 'readonly'
            ? (elsewhere() ? `Read-only: ${who()}` : 'Read-only')
            : state() === 'warn'
              ? `Open on ${who()}`
              : `Couldn't check ${(p()?.unchecked ?? []).join(', ')}`}
        </span>
        <Show when={state() === 'warn' && p()?.first === false}>
          <button type="button" class="doc-open-pill-act" onClick={openReadOnly}>Open read-only</button>
        </Show>
        <Show when={state() === 'readonly'}>
          <button type="button" class="doc-open-pill-act" onClick={editAnyway}>Edit anyway</button>
        </Show>
      </div>
    </Show>
  );
}
